import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import api from '../../../services/api';

/**
 * A service group's safety monitoring settings (14 Sep 2026): whether it overrides the
 * platform's alert notification mode, whether it appears in the daily safety summary, and
 * its own summary recipients (who get only this group's section).
 */

const unwrap = (res) => (res && res.data !== undefined ? res.data : res);

const MODE_LABELS = {
  full: 'Full',
  first_notice_only: 'First notice only',
  dashboard_only: 'Dashboard only'
};

const MODE_HELP = {
  full: 'Every notification, including overdue re-sends and escalation to supervisors and admins.',
  first_notice_only: 'The first notice for each alert only. Nothing is repeated or escalated.',
  dashboard_only: 'Alerts are recorded and shown on dashboards. No email, SMS or real-time notice.'
};

const ServiceGroupSafetyMonitoring = ({ groupId, groupName }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ overrideEnabled: false, notificationMode: '', includeInSummary: true, recipientsText: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const applyResponse = (body) => {
    setData(body);
    setForm({
      overrideEnabled: !!body?.overrides?.overrideEnabled,
      notificationMode: body?.overrides?.notificationMode || '',
      includeInSummary: body?.overrides?.includeInSummary !== false,
      recipientsText: (body?.overrides?.summaryRecipients || []).join('\n')
    });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      applyResponse(unwrap(await api.get(`/service-groups/${groupId}/safety-monitoring`)));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { if (groupId) load(); }, [groupId, load]);

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const body = unwrap(await api.put(`/service-groups/${groupId}/safety-monitoring`, {
        overrideEnabled: form.overrideEnabled,
        notificationMode: form.overrideEnabled ? form.notificationMode || null : (form.notificationMode || null),
        includeInSummary: form.includeInSummary,
        summaryRecipients: form.recipientsText
      }));
      applyResponse(body);
      setSuccess('Saved.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!groupId) return null;
  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  const effective = data?.effective || { mode: 'full', source: 'platform' };
  const platform = data?.platform || {};

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Safety monitoring for {groupName || data?.name}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity={effective.mode === 'full' ? 'info' : 'warning'} sx={{ mb: 2 }}>
        Alert notifications for this group: <strong>{MODE_LABELS[effective.mode]}</strong>
        {' '}({effective.source === 'service_group' ? 'this group\'s setting' : 'platform default'}). {MODE_HELP[effective.mode]}
      </Alert>

      {!platform.allowServiceGroupOverride && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Service group overrides are turned off in Admin → Safety Monitoring, so the platform default
          ({MODE_LABELS[platform.notificationMode]}) applies whatever is set here.
        </Alert>
      )}

      <FormControlLabel
        control={<Switch checked={form.overrideEnabled} onChange={e => setForm(f => ({ ...f, overrideEnabled: e.target.checked }))} />}
        label="Override the platform notification mode for this group"
      />
      <Box sx={{ mt: 1, mb: 3 }}>
        <TextField
          select
          label="Notification mode"
          value={form.notificationMode}
          onChange={e => setForm(f => ({ ...f, notificationMode: e.target.value }))}
          disabled={!form.overrideEnabled}
          sx={{ minWidth: 260 }}
          helperText={form.notificationMode ? MODE_HELP[form.notificationMode] : 'Choose a mode to override the platform default'}
        >
          {(data?.modes || Object.keys(MODE_LABELS)).map(m => <MenuItem key={m} value={m}>{MODE_LABELS[m]}</MenuItem>)}
        </TextField>
      </Box>

      <Typography variant="subtitle1" gutterBottom>Daily safety summary</Typography>
      {!platform.dailySummaryEnabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The daily summary is currently turned off in Admin → Safety Monitoring.
        </Typography>
      )}
      <FormControlLabel
        control={<Switch checked={form.includeInSummary} onChange={e => setForm(f => ({ ...f, includeInSummary: e.target.checked }))} />}
        label="Include this group in the daily summary"
      />
      <TextField
        label="Group summary recipients"
        value={form.recipientsText}
        onChange={e => setForm(f => ({ ...f, recipientsText: e.target.value }))}
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 1 }}
        helperText="One email address per line. They receive only this group's section."
      />

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Box>
    </Box>
  );
};

export default ServiceGroupSafetyMonitoring;
